import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Folder, Library, AlertCircle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  WodStructure,
  WodElement,
  WodMovement,
  WodGroup,
  WodElementType,
  isGroup,
  WodStructureUtils
} from '../../types/wod.types';
import { StructureNode } from './StructureNode';
import { StructurePreview } from './StructurePreview';
import { MovementDialog } from './MovementDialog';
import { GroupDialog } from './GroupDialog';
import { TemplatesDialog } from './TemplatesDialog';

interface WODStructureBuilderProps {
  structure: WodStructure;
  onChange: (structure: WodStructure) => void;
}

export const WODStructureBuilder: React.FC<WODStructureBuilderProps> = ({
  structure,
  onChange
}) => {
  const { t } = useTranslation();
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [editingElement, setEditingElement] = useState<WodElement | undefined>();
  const [parentGroupId, setParentGroupId] = useState<string | undefined>();
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Vérifie si un groupe (ou ses ancêtres) est AMRAP
  const isInAmrapContext = (groupId: string | undefined, elementId?: string): boolean => {
    if (!groupId && !elementId) return false;

    // Fonction récursive pour trouver le parent d'un élément
    const findParentGroup = (elements: WodElement[], targetId: string): WodGroup | null => {
      for (const element of elements) {
        if (isGroup(element)) {
          // Vérifie si le target est un enfant direct
          if (element.children.some(child => child.id === targetId)) {
            return element;
          }
          // Cherche récursivement dans les enfants
          const found = findParentGroup(element.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Vérifie si un groupe ou ses ancêtres sont AMRAP
    const checkAmrapAncestors = (gId: string): boolean => {
      const group = WodStructureUtils.findElementById(structure, gId);
      if (group && isGroup(group) && group.isAmrap) {
        return true;
      }
      // Cherche le parent de ce groupe
      const parent = findParentGroup(structure.elements, gId);
      if (parent) {
        if (parent.isAmrap) return true;
        return checkAmrapAncestors(parent.id);
      }
      return false;
    };

    // Si on a un groupId (ajout dans un groupe), vérifier ce groupe
    if (groupId) {
      return checkAmrapAncestors(groupId);
    }

    // Si on édite un élément, trouver son parent
    if (elementId) {
      const parent = findParentGroup(structure.elements, elementId);
      if (parent) {
        if (parent.isAmrap) return true;
        return checkAmrapAncestors(parent.id);
      }
    }

    return false;
  };

  // Ajouter un mouvement à la racine
  const handleAddMovementRoot = () => {
    setEditingElement(undefined);
    setParentGroupId(undefined);
    setMovementDialogOpen(true);
  };

  // Ajouter un groupe à la racine
  const handleAddGroupRoot = () => {
    setEditingElement(undefined);
    setParentGroupId(undefined);
    setGroupDialogOpen(true);
  };

  // Ajouter un élément dans un groupe
  const handleAddToGroup = (groupId: string, type: 'movement' | 'group') => {
    setEditingElement(undefined);
    setParentGroupId(groupId);

    if (type === 'movement') {
      setMovementDialogOpen(true);
    } else {
      setGroupDialogOpen(true);
    }
  };

  // Éditer un élément existant
  const handleEdit = (element: WodElement) => {
    setEditingElement(element);
    setParentGroupId(undefined);

    if (element.type === WodElementType.MOVEMENT) {
      setMovementDialogOpen(true);
    } else {
      setGroupDialogOpen(true);
    }
  };

  // Sauvegarder un mouvement
  const handleSaveMovement = (movement: WodMovement) => {
    let newStructure: WodStructure;

    if (editingElement) {
      // Mode édition
      newStructure = WodStructureUtils.updateElement(structure, movement.id, movement);
    } else if (parentGroupId) {
      // Ajouter à un groupe parent
      newStructure = addElementToGroup(structure, parentGroupId, movement);
    } else {
      // Ajouter à la racine
      newStructure = {
        elements: [...structure.elements, movement]
      };
    }

    onChange(newStructure);
    validate(newStructure);
  };

  // Sauvegarder un groupe
  const handleSaveGroup = (group: WodGroup) => {
    let newStructure: WodStructure;

    if (editingElement) {
      // Mode édition - conserver les enfants existants
      const existingGroup = WodStructureUtils.findElementById(structure, group.id);
      if (existingGroup && isGroup(existingGroup)) {
        group.children = existingGroup.children;
      }
      newStructure = WodStructureUtils.updateElement(structure, group.id, group);
    } else if (parentGroupId) {
      // Ajouter à un groupe parent
      newStructure = addElementToGroup(structure, parentGroupId, group);
    } else {
      // Ajouter à la racine
      newStructure = {
        elements: [...structure.elements, group]
      };
    }

    onChange(newStructure);
    validate(newStructure);
  };

  // Supprimer un élément
  const handleDelete = (elementId: string) => {
    const newStructure = WodStructureUtils.removeElement(structure, elementId);
    onChange(newStructure);
    validate(newStructure);
  };

  // Dupliquer un élément
  const handleDuplicate = (elementId: string) => {
    const newStructure = WodStructureUtils.duplicateAndInsertAfter(structure, elementId);
    onChange(newStructure);
    validate(newStructure);
  };

  // Indenter un élément
  const handleIndent = (elementId: string) => {
    const newStructure = WodStructureUtils.indentElement(structure, elementId);
    onChange(newStructure);
    validate(newStructure);
  };

  // Désindenter un élément
  const handleOutdent = (elementId: string) => {
    const newStructure = WodStructureUtils.outdentElement(structure, elementId);
    onChange(newStructure);
    validate(newStructure);
  };

  // Charger un template
  const handleSelectTemplate = (templateStructure: WodStructure) => {
    onChange(templateStructure);
    validate(templateStructure);
  };

  // Gérer le drag & drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    console.log('🎯 Drag & Drop:', {
      source: source.droppableId,
      destination: destination?.droppableId,
      draggableId,
      sourceIndex: source.index,
      destinationIndex: destination?.index
    });

    // Abandonné en dehors d'une zone de drop
    if (!destination) {
      return;
    }

    // Même position : pas de changement
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('⚠️ Même position, pas de changement');
      return;
    }

    let newStructure: WodStructure;

    // CAS 1: Drop sur un header de groupe (pour ajouter en tant qu'enfant)
    if (destination.droppableId.startsWith('group-') && !destination.droppableId.includes('children')) {
      console.log('📦 Drop sur header de groupe');
      const targetGroupId = destination.droppableId.replace('group-', '');

      if (draggableId === targetGroupId) {
        console.log('⚠️ Cannot drop group on itself');
        return;
      }

      newStructure = WodStructureUtils.moveElementToGroup(structure, draggableId, targetGroupId);
    }
    // CAS 2: Réordonnancement au sein du même groupe d'enfants
    else if (source.droppableId === destination.droppableId && source.droppableId.startsWith('group-children-')) {
      console.log('🔄 Réordonnancement dans le même groupe');
      const groupId = source.droppableId.replace('group-children-', '');
      newStructure = WodStructureUtils.reorderGroupChildren(structure, groupId, source.index, destination.index);
    }
    // CAS 3: Déplacement entre deux groupes différents
    else if (source.droppableId.startsWith('group-children-') && destination.droppableId.startsWith('group-children-')) {
      console.log('🔀 Déplacement entre groupes');
      const targetGroupId = destination.droppableId.replace('group-children-', '');
      newStructure = WodStructureUtils.moveElementToGroup(structure, draggableId, targetGroupId);
    }
    // CAS 4: Tentative de sortir d'un groupe vers la racine - BLOQUÉ
    else if (source.droppableId.startsWith('group-children-') && destination.droppableId === 'wod-structure') {
      console.log('🚫 Enfant ne peut pas sortir du groupe via drag & drop. Utilisez le bouton "Désindenter"');
      return;
    }
    // CAS 5: Entrer dans un groupe depuis la racine
    else if (source.droppableId === 'wod-structure' && destination.droppableId.startsWith('group-children-')) {
      console.log('⬇️ Entrer dans un groupe depuis racine');
      const targetGroupId = destination.droppableId.replace('group-children-', '');
      newStructure = WodStructureUtils.moveElementToGroup(structure, draggableId, targetGroupId);
    }
    // CAS 6: Réorganisation à la racine
    else if (source.droppableId === 'wod-structure' && destination.droppableId === 'wod-structure') {
      console.log('🔄 Réorganisation à la racine');
      newStructure = WodStructureUtils.reorderElements(structure, source.index, destination.index);
    }
    else {
      console.log('❌ Cas non géré:', { source: source.droppableId, destination: destination.droppableId });
      return;
    }

    console.log('✅ Structure mise à jour');
    console.log('New structure:', JSON.stringify(newStructure, null, 2));

    // Force React à détecter le changement avec un deep clone
    const forceNewStructure = JSON.parse(JSON.stringify(newStructure));

    onChange(forceNewStructure);
    validate(forceNewStructure);
  };

  // Fonction utilitaire pour ajouter un élément à un groupe
  const addElementToGroup = (
    currentStructure: WodStructure,
    groupId: string,
    element: WodElement
  ): WodStructure => {
    function addToGroup(elements: WodElement[]): WodElement[] {
      return elements.map(el => {
        if (el.id === groupId && isGroup(el)) {
          return {
            ...el,
            children: [...el.children, element]
          };
        }
        if (isGroup(el)) {
          return {
            ...el,
            children: addToGroup(el.children)
          };
        }
        return el;
      });
    }

    return {
      elements: addToGroup(currentStructure.elements)
    };
  };

  // Valider la structure
  const validate = (structureToValidate: WodStructure = structure) => {
    const errors = WodStructureUtils.validateStructure(structureToValidate);
    setValidationErrors(errors);
  };

  return (
    <div>
      {/* Toolbar */}
      <Card className="mb-4 border-border">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
            {t('wods.structureBuilder.title')}
          </h3>

          <div className="flex justify-between gap-2 flex-wrap">
            <div className="flex gap-2">
              <Button onClick={handleAddMovementRoot}>
                <Dumbbell className="h-4 w-4 mr-2" />
                {t('wods.structureBuilder.addMovement')}
              </Button>

              <Button variant="outline" onClick={handleAddGroupRoot}>
                <Folder className="h-4 w-4 mr-2" />
                {t('wods.structureBuilder.addGroup')}
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={() => setTemplatesDialogOpen(true)}
            >
              <Library className="h-4 w-4 mr-2" />
              {t('wods.structureBuilder.famousWods')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Erreurs de validation */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('wods.structureBuilder.validationErrors')}</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 mt-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Arbre de structure */}
      <Card className="mb-4 border-border min-h-[200px]">
        {structure.elements.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground italic text-sm">
              {t('wods.structureBuilder.emptyStructure')}
            </p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="wod-structure" type="WOD_ELEMENT">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`p-2 transition-colors ${
                    snapshot.isDraggingOver ? 'bg-white/[0.05]' : ''
                  }`}
                >
                  {structure.elements.map((element, index) => (
                    <Draggable key={element.id} draggableId={element.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.9 : 1,
                            transform: snapshot.isDragging
                              ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                              : provided.draggableProps.style?.transform,
                          }}
                          className={snapshot.isDragging ? 'shadow-lg shadow-primary/50' : ''}
                        >
                          <StructureNode
                            element={element}
                            level={0}
                            structure={structure}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onDuplicate={handleDuplicate}
                            onAddToGroup={handleAddToGroup}
                            onIndent={handleIndent}
                            onOutdent={handleOutdent}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Card>

      {/* Aperçu */}
      <StructurePreview
        structure={structure}
        expanded={previewExpanded}
        onToggle={() => setPreviewExpanded(!previewExpanded)}
      />

      {/* Dialogs */}
      <MovementDialog
        open={movementDialogOpen}
        movement={editingElement?.type === WodElementType.MOVEMENT ? editingElement as WodMovement : undefined}
        onClose={() => {
          setMovementDialogOpen(false);
          setEditingElement(undefined);
          setParentGroupId(undefined);
        }}
        onSave={handleSaveMovement}
        disableTiebreak={isInAmrapContext(parentGroupId, editingElement?.id)}
      />

      <GroupDialog
        open={groupDialogOpen}
        group={editingElement?.type === WodElementType.GROUP ? editingElement as WodGroup : undefined}
        onClose={() => {
          setGroupDialogOpen(false);
          setEditingElement(undefined);
          setParentGroupId(undefined);
        }}
        onSave={handleSaveGroup}
      />

      <TemplatesDialog
        open={templatesDialogOpen}
        onClose={() => setTemplatesDialogOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </div>
  );
};
